import Sortable from 'sortablejs';
import {getImageFileError, getValidationErrorForLargeUploads, resetFormErrors, showFormErrors} from '../../../utils/form';
import {apiUrl} from '../../../utils/urlGenerator';
import * as notification from '../../notifications';

const maxImages = 30;
/** Must match CustomFieldImageModule::MAX_FILE_SIZE_KB, which Laravel counts in 1024-byte KB. */
const maxFileSizeKb = 5000;
const allowedImageMimeTypes = ['image/jpeg', 'image/png'];

const normalizeItems = (items) => Array.isArray(items)
  ? items.filter((item) => item && typeof item.path === 'string').map((item, index) => ({
    path: item.path,
    caption: typeof item.caption === 'string' ? item.caption : '',
    sort_order: index,
    persisted: true,
  }))
  : [];

const initImageGallery = (container) => {
  const $container = $(container);

  if ($container.data('image-gallery-initialized')) {
    return;
  }

  $container.data('image-gallery-initialized', true);

  const fieldId = $container.data('field-id');
  const captionMode = $container.data('caption-mode');
  const $form = $container.closest('form');
  const $list = $('.form-field-image-gallery-list', $container);
  const $valueInput = $('.form-field-image-gallery-value', $container);
  const $fileInput = $('.form-field-image-gallery-input', $container);
  const $dropzone = $('.form-field-image-gallery-dropzone', $container);
  const $reorderHint = $('.form-field-image-gallery-reorder-hint', $container);
  let items = normalizeItems(JSON.parse($container.attr('data-items') || '[]'));

  const syncValue = () => {
    items = items.map((item, index) => ({...item, sort_order: index}));
    $valueInput.val(JSON.stringify(items.map(({path, caption, sort_order}) => ({path, caption, sort_order}))));
  };

  const removeItem = async (index) => {
    const [item] = items.splice(index, 1);
    render();
    syncValue();

    if (item.persisted) {
      return;
    }

    try {
      await axios.delete(apiUrl('/temporary_images'), {
        data: {
          url: item.path,
          form_field_id: fieldId,
        },
        headers: {
          'X-CSRF-TOKEN': $("input[name='_token']", $form).val() || window.$jBoard.csrf_token,
        },
      });
    } catch (error) {
      items.splice(index, 0, item);
      render();
      syncValue();
      notification.danger('Unable to delete image. Please try again.');
    }
  };

  const render = () => {
    $list.empty();
    $list.toggleClass('mt-3', items.length > 0);
    $reorderHint.toggleClass('d-none', items.length < 2);

    items.forEach((item, index) => {
      const $itemWrapper = $('<div>', {class: 'form-group form-field-image-gallery-item-wrapper mb-3'});

      const $item = $('<div>', {
        class: 'form-field-image-gallery-item d-flex align-items-center p-3',
      });

      const $handle = $('<span>', {
        class: 'form-field-image-gallery-handle',
        text: '⠿',
        'aria-label': 'Drag to reorder',
      });

      const $thumbnail = $('<div>', {class: 'form-field-image-gallery-thumbnail'});

      $thumbnail.append($('<img>', {src: item.path, alt: ''}));
      $item.append($handle, $thumbnail);

      if (captionMode !== 'off') {
        const inputId = `v-custom_fields-${fieldId}-value-${index}-caption`;
        const $caption = $('<input>', {
          class: 'form-control jb-form-control form-field-image-gallery-caption w-100',
          id: inputId,
          maxlength: 120,
          placeholder: 'Add a caption',
          type: 'text',
          value: item.caption,
        });

        $caption.on('input', function () {
          items[index].caption = $(this).val();
          syncValue();
        });

        const $captionContainer = $('<div>', {class: 'form-group mb-0 form-field-image-gallery-caption-container'});
        $captionContainer.append($caption);
        $item.append($captionContainer);
      }

      const $remove = $('<button>', {
        class: `p-0 form-field-image-gallery-remove${captionMode === 'off' ? ' ml-auto' : ''}`,
        type: 'button',
        'aria-label': 'Remove image',
        html: '&times;',
      });

      $remove.on('click', () => removeItem(index));

      $item.append($remove);
      $itemWrapper.append($item);
      $list.append($itemWrapper);
    });
  };

  const uploadFile = async (file) => {
    const pickedFileError = getImageFileError(file, {
      allowedMimeTypes: allowedImageMimeTypes,
      maxFileSizeKb: maxFileSizeKb,
      invalidTypeMessage: 'You can upload only JPG, JPEG, or PNG images.',
      tooLargeMessage: 'The uploaded image may not be greater than 5 MB.',
    });

    if (pickedFileError) {
      notification.danger(pickedFileError);
      return;
    }

    if (items.length >= maxImages) {
      notification.danger('You can upload a maximum of 30 images.');
      return;
    }

    const data = new FormData();
    data.append('file', file);

    try {
      const response = await axios.post(`${$jBoard.config.main_api_url}/images/CustomField`, data, {
        headers: {'X-Use-Team-Trans': 'true'},
      });

      items.unshift({path: response.data.url, caption: '', sort_order: 0, persisted: false});

      render();
      syncValue();
      resetFormErrors($form);
    } catch (error) {
      if (!error.response) {
        notification.danger('Unable to upload image. Please try again.');
        return;
      }

      const response = getValidationErrorForLargeUploads(error.response, `custom_fields.${fieldId}.value`);

      if (response) {
        showFormErrors($form, response);
      }
    }
  };

  const uploadFiles = async (files) => {
    for (const file of files) {
      await uploadFile(file);
    }

    $fileInput.val('');
  };

  $('.form-field-image-gallery-choose', $container).on('click', (event) => {
    event.preventDefault();
    $fileInput.trigger('click');
  });

  $fileInput.on('change', () => uploadFiles(Array.from($fileInput[0].files || [])));

  $dropzone.on('dragover', (event) => {
    event.preventDefault();
    $dropzone.addClass('is-dragover');
  }).on('dragleave drop', (event) => {
    event.preventDefault();
    $dropzone.removeClass('is-dragover');
  }).on('drop', (event) => uploadFiles(Array.from(event.originalEvent.dataTransfer.files || [])));

  /**
   * Bound once to the list container, which render() only empties - never
   * replaces. Re-creating it per render would mean destroying the instance from
   * inside its own onEnd, while SortableJS is still tearing the drag down.
   */
  Sortable.create($list[0], {
    direction: 'vertical',
    draggable: '.form-field-image-gallery-item-wrapper',
    handle: '.form-field-image-gallery-handle',
    onEnd: ({oldIndex, newIndex}) => {
      if (oldIndex === newIndex) {
        return;
      }

      items.splice(newIndex, 0, items.splice(oldIndex, 1)[0]);

      /** Re-render so the caption and remove handlers bind to the new indexes. */
      render();
      syncValue();
    },
  });

  render();
  syncValue();
};

const initImageGalleries = () => {
  $('.form-field-image-gallery').each(function () {
    initImageGallery(this);
  });
};

$(initImageGalleries);
document.addEventListener('jb.section.rendered', initImageGalleries);
